<template>
  <div>
    <LISAMenu v-if="isLISA3" :items="formattedMenuItems" :placement="'right-start'">
      <template #trigger="{ triggerAttrs }">
        <LISAButton v-bind="triggerAttrs" class="p-0" icon="fas fa-bars" text> </LISAButton>
      </template>
    </LISAMenu>
    <LISAButton
      v-if="!isLISA3"
      type="button"
      class="p-button-sm p-button-outlined p-2"
      style="border: 0"
      :style="
        changeColorAndBackgroundOnToggle ? 'background: var(--p-primary-color);color: white' : ''
      "
      :class="archivingBlocked ? 'p-button-primary' : 'p-button-success'"
      @click="toggle"
    >
      <i class="fas fa-bars" />
    </LISAButton>
    <Menu
      v-if="!isLISA3"
      ref="menu"
      class="EventButtonList"
      :model="menuItems"
      popup
      @show="changeColorAndBackgroundOnToggle = !changeColorAndBackgroundOnToggle"
      @hide="changeColorAndBackgroundOnToggle = !changeColorAndBackgroundOnToggle"
    >
      <!--Style needs to be inline because as class the p-disabled class will override it!!!!-->
      <template #item="{ item }">
        <a
          v-tooltip.right.hover="item.key"
          class="p-menu-item-link"
          style="pointer-events: all !important"
          tabindex="-1"
        >
          <span v-if="item.icon" class="p-menuitem-icon" :class="item.icon"></span>
          <span class="p-menu-item-label">{{ item.label }}</span>
        </a>
      </template>
    </Menu>

    <MoveEventModal v-if="showMoveEventModal" v-model="showMoveEventModal" :event="event" />

    <EventLinkageModal
      v-if="eventLinkageModalVisibility"
      v-model:visible="eventLinkageModalVisibility"
      :event="event"
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType, ref, computed, inject } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import Menu from "primevue/menu";
import LISAButton from "@LISAPrime/LISAButton.vue";
import type { MenuItem } from "primevue/menuitem";
import vTooltip from "primevue/tooltip";
import {
  type EventListEventFieldsFragment,
  useArchiveEventMutation,
  useReactivateEventMutation,
  EventType,
} from "@/graphql";
import useEventListZIndexFix from "../../EventListZIndexFix";
import MoveEventModal from "../MoveEventModal.vue";
import EventLinkageModal from "../EventLinkageModal.vue";
import { useEventArchive } from "../composables/eventArchive";
import { LISAMenu, type LISAMenuGroup, type LISAMenuItem } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3.ts";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
const props = defineProps({
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
});
const moveEventModal = ref<InstanceType<typeof MoveEventModal> | null>(null);
const showMoveEventModal = ref(false);
const changeColorAndBackgroundOnToggle = ref(false);

const { t } = useTranslation();
const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const { archivingBlocked, archivingBlockedReason } = useEventArchive(
  computed(() => ({
    preventsArchiving: props.event.preventsArchiving,
    actions: props.event.actions,
    event: props.event.__typename as EventType,
  })),
);

const { show, hide } = useEventListZIndexFix();
defineExpose({
  show,
  hide,
  moveEventModal,
});
const { mutate: archiveEvent } = useArchiveEventMutation(() => ({
  variables: {
    id: props.event.id,
    eventType: props.event.__typename as EventType,
    boardId: boardId.value ?? undefined,
  },
  optimisticResponse: {
    archiveEvent: {
      __typename: props.event.__typename,
      id: props.event.id,
      deletedAt: DateTime.now().toISO(),
      updatedAt: DateTime.now().toISO(),
    },
  },
}));

const { mutate: reactivateEvent } = useReactivateEventMutation(() => ({
  variables: {
    id: props.event.id,
    eventType: props.event.__typename as EventType,
    boardId: boardId.value ?? undefined,
  },
}));

const eventLinkageModalVisibility = ref(false);

function openEventLinkage() {
  eventLinkageModalVisibility.value = true;
}

const menu = ref<InstanceType<typeof Menu>>();
const formattedMenuItems = computed<LISAMenuGroup[]>(() =>
  menuItems.value.map((el) => {
    return {
      ...el,
      label: el.label as string,
      items: el.items as LISAMenuItem[],
    };
  }),
);
const menuItems = computed(() => {
  let entries = <(MenuItem & { id: number })[]>[];

  if (props.event.deletedAt) {
    entries.push({
      id: 0,
      label: t("components.eventActionBoard.IssueConfigButtonList.reactivateIssue").toString(),
      icon: "fas fa-trash-restore-alt text-primary",
      command: () => reactivateEvent(),
      callback: () => reactivateEvent(),
    });
  } else {
    entries = [
      {
        id: 0,
        label: t("components.eventActionBoard.IssueConfigButtonList.escalateDelegate").toString(),
        icon: "pi pi-directions text-primary",
        command: () => {
          showMoveEventModal.value = true;
        },
        callback: () => {
          showMoveEventModal.value = true;
        },
      },
      {
        id: 1,
        disabled: archivingBlocked.value,
        class: archivingBlocked.value
          ? "eventLinkedArchiveScopeDisable"
          : "eventLinkedArchiveScope",
        label: t("components.eventActionBoard.IssueConfigButtonList.archive").toString(),
        icon: "fas fa-archive",
        key: archivingBlocked.value ? archivingBlockedReason.value : undefined,
        command: () => {
          archiveEvent();
        },
        callback: () => {
          archiveEvent();
        },
      },
      {
        id: 5,
        label: t("components.eventActionBoard.IssueConfigButtonList.eventLinkage").toString(),
        icon: "fas fa-link text-primary",
        command: () => {
          openEventLinkage();
        },
        callback: () => {
          openEventLinkage();
        },
      },
    ];
  }
  return [
    {
      label: t("global.edit").toString(),
      items: entries,
    },
  ];
});

const toggle = (event: Event) => {
  menu.value?.toggle(event);
};
</script>

<style scoped lang="scss">
.buttonGroup {
  flex-shrink: 0;

  :deep([data-lisa-popover-trigger]) {
    min-width: 2rem;
  }
}
</style>

<style lang="scss">
@use "./EventButton.scss" as *;
.eventLinkedArchiveScope .p-menuitem-text {
  color: var(--p-success-color) !important;
}
.eventLinkedArchiveScope .p-menuitem-icon {
  color: var(--p-success-color) !important;
}
.eventLinkedArchiveScopeDisable .p-menuitem-icon {
  color: var(--p-secondary-color) !important;
}
</style>
